import React, { useEffect, useState } from "react";
import api from "../../api";
import { toast } from "sonner";
import { IndianRupee, Save, Loader2, Sparkles, Info } from "lucide-react";

const PRODUCT_LABEL = {
  jidnyasa: "Jidnyasa only",
  neuroeq:  "NeuroEQ only",
  both:     "Both programs (bundle)",
};

function computeYearly(monthly, pct) {
  const raw = Number(monthly) * 12 * (1 - Number(pct) / 100);
  return Math.round(raw / 10) * 10;
}

export default function AdminPricing() {
  const [form, setForm] = useState(null);
  const [plans, setPlans] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/pricing");
      setForm(r.data.config);
      setPlans(r.data.plans);
    } catch (e) {
      toast.error("Could not load pricing");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async (e) => {
    e.preventDefault();
    // Basic validation
    for (const k of ["jidnyasa_monthly_inr", "neuroeq_monthly_inr", "both_monthly_inr"]) {
      const v = Number(form[k]);
      if (!Number.isFinite(v) || v < 49 || v > 100000) {
        toast.error("Prices must be between ₹49 and ₹1,00,000");
        return;
      }
    }
    const pct = Number(form.yearly_discount_pct);
    if (!Number.isFinite(pct) || pct < 0 || pct > 90) {
      toast.error("Yearly discount must be between 0 and 90");
      return;
    }
    setSaving(true);
    try {
      const r = await api.put("/admin/pricing", {
        jidnyasa_monthly_inr: Number(form.jidnyasa_monthly_inr),
        neuroeq_monthly_inr:  Number(form.neuroeq_monthly_inr),
        both_monthly_inr:     Number(form.both_monthly_inr),
        yearly_discount_pct:  pct,
      });
      setPlans(r.data.plans);
      toast.success("Pricing updated · live for new checkouts");
    } catch (err) {
      toast.error(err?.response?.data?.detail?.[0]?.msg
                    || err?.response?.data?.detail
                    || "Could not save");
    } finally { setSaving(false); }
  };

  if (loading || !form) {
    return <div className="text-muted-foreground">Loading pricing…</div>;
  }

  return (
    <div className="max-w-3xl" data-testid="admin-pricing-page">
      <div className="flex items-start gap-3 mb-6">
        <IndianRupee className="w-6 h-6 text-[hsl(var(--neu-orange))] mt-1"/>
        <div>
          <h1 className="font-display text-3xl">Subscription pricing</h1>
          <p className="text-sm text-muted-foreground">
            Set the base monthly price for each plan and the yearly discount.
            Yearly rates are computed automatically (rounded to the nearest ₹10).
            Changes go live for the very next checkout — no redeploy needed.
          </p>
        </div>
      </div>

      <div className="rounded-2xl bg-[hsl(var(--neu-orange))]/10 border border-[hsl(var(--neu-orange))]/25 p-4 mb-6 flex gap-3 text-sm"
            data-testid="admin-pricing-warning">
        <Info className="w-4 h-4 text-[hsl(var(--neu-orange))] mt-0.5 shrink-0"/>
        <div>
          <b>Existing subscribers are unaffected.</b> These prices apply to <i>new</i> Razorpay
          orders created after you save. Historical payments and running subscriptions keep
          the amount they were charged at.
        </div>
      </div>

      <form onSubmit={save} className="card-elevated p-6 space-y-5">
        {[
          { k: "jidnyasa_monthly_inr", label: "Jidnyasa only · monthly (₹)",
            testid: "admin-pricing-jidnyasa" },
          { k: "neuroeq_monthly_inr",  label: "NeuroEQ only · monthly (₹)",
            testid: "admin-pricing-neuroeq" },
          { k: "both_monthly_inr",     label: "Both programs bundle · monthly (₹)",
            testid: "admin-pricing-both" },
        ].map((row) => {
          const yearly = computeYearly(form[row.k], form.yearly_discount_pct);
          return (
            <div key={row.k} className="grid grid-cols-1 sm:grid-cols-[1fr,auto] gap-3 items-end">
              <div>
                <label className="text-xs text-muted-foreground">{row.label}</label>
                <input type="number" min="49" max="100000" step="10"
                        className="field text-lg"
                        value={form[row.k]}
                        onChange={(e) => set(row.k, e.target.value)}
                        data-testid={row.testid}/>
              </div>
              <div className="text-sm text-muted-foreground bg-[hsl(var(--neu-paper))] rounded-2xl px-4 py-3">
                Yearly: <b className="text-[hsl(var(--neu-ink))]">₹{yearly.toLocaleString("en-IN")}</b>
                <span className="ml-2 text-xs">· ~₹{Math.round(yearly / 12)}/mo</span>
              </div>
            </div>
          );
        })}

        <div className="grid grid-cols-1 sm:grid-cols-[1fr,auto] gap-3 items-end pt-2 border-t border-black/5">
          <div>
            <label className="text-xs text-muted-foreground">Yearly discount (%)</label>
            <input type="number" min="0" max="90" step="1"
                    className="field text-lg"
                    value={form.yearly_discount_pct}
                    onChange={(e) => set("yearly_discount_pct", e.target.value)}
                    data-testid="admin-pricing-discount"/>
          </div>
          <div className="text-xs text-muted-foreground max-w-[220px]">
            Applied to all three products. 20% = the "save 20%" badge shown on the pricing page.
          </div>
        </div>

        <button type="submit" disabled={saving}
                 className="btn-primary text-base"
                 data-testid="admin-pricing-save">
          {saving ? <Loader2 className="w-4 h-4 animate-spin"/> : <><Save className="w-4 h-4"/> Save & go live</>}
        </button>
      </form>

      {plans && (
        <div className="mt-8">
          <div className="text-xs uppercase tracking-widest text-muted-foreground mb-3 flex items-center gap-2">
            <Sparkles className="w-3.5 h-3.5"/> Live preview · what parents see
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {["jidnyasa", "both", "neuroeq"].map((k) => (
              <div key={k} className={`rounded-2xl p-5 ${
                k === "both" ? "bg-[hsl(var(--neu-ink))] text-white" : "card-soft"
              }`} data-testid={`admin-pricing-preview-${k}`}>
                <div className={`text-[10px] uppercase tracking-widest ${
                  k === "both" ? "text-white/70" : "text-muted-foreground"
                }`}>{PRODUCT_LABEL[k]}</div>
                <div className="mt-2 font-display text-2xl">
                  ₹{plans[k].yearly.final_inr.toLocaleString("en-IN")}
                  <span className={`text-xs font-normal ml-1 ${k === "both" ? "text-white/70" : "text-muted-foreground"}`}>/ yr</span>
                </div>
                <div className={`text-xs mt-1 ${k === "both" ? "text-white/70" : "text-muted-foreground"}`}>
                  or ₹{plans[k].monthly.final_inr.toLocaleString("en-IN")} / month
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
